import { resetAgentState } from '@/agent/dispatcher';
import { clearRenderHistory } from '@/agent/tools/render';
import { refreshLayerMetadata, findLayer } from '@/map/layers/registry';
import {
  getBufferLayer,
  getHighlightLayer,
  getMarkerLayer,
  getSelectionLayer,
} from '@/map/mapStore';
import { clearMeasurements } from '@/map/measurements';
import { clearSketches } from '@/map/sketches';
import { clearUserSelection } from '@/map/selection';
import { useWidgetStore } from '@/state/widgetStore';
import { useChatStore } from './chatStore';

export type SlashOutcome =
  | { handled: false }
  | { handled: true; reply: string };

export type SlashCommandMeta = {
  name: `/${string}`;
  description: string;
};

export const SLASH_COMMANDS: SlashCommandMeta[] = [
  { name: '/help', description: 'Show slash command help.' },
  { name: '/new-chat', description: 'Start a new empty chat session.' },
  { name: '/reset', description: 'Clear all chats and tool handles.' },
  { name: '/reset-chat', description: 'Clear only the current chat and tool handles.' },
  { name: '/clear-selection', description: 'Drop the current click selection.' },
  {
    name: '/clear-map',
    description: 'Clear highlights, markers, buffers, selection, sketches, and measurements.',
  },
  { name: '/refresh-layer', description: 'Re-probe a layer (invalidates cache).' },
  { name: '/clear-charts', description: 'Close all open chart cards.' },
];

export function matchSlashCommands(draft: string): SlashCommandMeta[] {
  const trimmed = draft.trim();
  if (!/^\/[\w-]*$/.test(trimmed)) return [];
  const lower = trimmed.toLowerCase();
  return SLASH_COMMANDS.filter((cmd) => cmd.name.startsWith(lower));
}

const HELP = [
  '**Slash commands**',
  ...SLASH_COMMANDS.map((cmd) => `\`${cmd.name}\` — ${cmd.description}`),
  '`/refresh-layer <id>` — requires a layer id argument',
  '',
  '**Keyboard shortcuts**',
  '`Ctrl+B` — toggle sidebar',
  '`Ctrl+J` — toggle chat panel',
  '`Ctrl+K` — focus chat composer',
].join('\n');

export async function tryHandleSlashCommand(text: string): Promise<SlashOutcome> {
  const trimmed = text.trim();
  if (!trimmed.startsWith('/')) return { handled: false };
  const [head, ...rest] = trimmed.slice(1).split(/\s+/);
  const command = head.toLowerCase();
  const arg = rest.join(' ').trim();

  switch (command) {
    case 'help':
      return { handled: true, reply: HELP };

    case 'reset':
      useChatStore.getState().resetAllChats();
      resetAgentState();
      return { handled: true, reply: 'All chat history and tool handles cleared.' };

    case 'reset-chat':
      useChatStore.getState().resetCurrentChat();
      resetAgentState();
      return { handled: true, reply: 'Current chat and tool handles cleared.' };

    case 'new-chat':
      useChatStore.getState().startNewChat();
      resetAgentState();
      return { handled: true, reply: 'Started a new chat.' };

    case 'clear-selection': {
      clearUserSelection(getSelectionLayer());
      return { handled: true, reply: 'Map selection cleared.' };
    }

    case 'clear-map': {
      getHighlightLayer()?.removeAll();
      getMarkerLayer()?.removeAll();
      getBufferLayer()?.removeAll();
      clearRenderHistory();
      clearUserSelection(getSelectionLayer());
      clearSketches();
      clearMeasurements();
      return {
        handled: true,
        reply: 'Map cleared (highlights, markers, buffers, selection, sketches, measurements).',
      };
    }

    case 'clear-charts':
      useWidgetStore.getState().clearCharts();
      return { handled: true, reply: 'All chart cards closed.' };

    case 'refresh-layer': {
      if (!arg) return { handled: true, reply: 'Usage: /refresh-layer <layer_id>' };
      if (!findLayer(arg)) {
        return { handled: true, reply: `Unknown layer id: \`${arg}\`` };
      }
      try {
        await refreshLayerMetadata(arg);
        return { handled: true, reply: `Re-probed layer \`${arg}\`.` };
      } catch (err) {
        return {
          handled: true,
          reply: `Failed to refresh \`${arg}\`: ${(err as Error).message}`,
        };
      }
    }

    default:
      return { handled: true, reply: `Unknown slash command: \`/${command}\`. Try /help.` };
  }
}
